<template>
  <DossierDocumentCard
    title="CNPJ – Comprovante de Inscrição e Situação Cadastral"
    :status-options="statusOptions"
    :multiple-status="false"
    :status-requires-validity="[]"
    :status-requires-upload="['possui_atualizado']"
    :explainer-text="explainerText"
    :mode="mode"
    :not-applicable="false"
    :start-collapsed="startCollapsed"
    v-model="formData"
    save-endpoint="/rest-home/dossier/cnpj"
    upload-endpoint="/rest-home/dossier/cnpj/upload"
    @saved="onSaved"
    @error="onError"
  >
    <!-- ─── Campos específicos do CNPJ (acima do status) ─── -->
    <template #fields-before>
      <div class="dossier-cnpj__fields">
        <!-- Número do CNPJ -->
        <div class="dossier-cnpj__field-group">
          <label class="dossier-cnpj__label dossier-cnpj__label--required">
            Número do CNPJ
          </label>

          <!-- Modo edição -->
          <v-text-field
            v-if="!isViewMode"
            v-model="formData.cnpj_number"
            v-mask="'##.###.###/####-##'"
            dense
            outlined
            hide-details="auto"
            placeholder="00.000.000/0000-00"
            :rules="[rules.required, rules.cnpj]"
            class="dossier-cnpj__input"
          />

          <!-- Modo visualização -->
          <span v-else class="dossier-cnpj__view-value">
            {{ formData.cnpj_number || "—" }}
          </span>
        </div>

        <!-- Data de abertura -->
        <div class="dossier-cnpj__field-group">
          <label class="dossier-cnpj__label dossier-cnpj__label--required">
            Data de abertura da empresa
          </label>

          <!-- Modo edição -->
          <v-text-field
            v-if="!isViewMode"
            v-model="formData.opening_date"
            type="date"
            dense
            outlined
            hide-details="auto"
            :rules="[rules.required]"
            class="dossier-cnpj__input dossier-cnpj__input--date"
          />

          <!-- Modo visualização -->
          <span v-else class="dossier-cnpj__view-value">
            <i class="fas fa-calendar-alt mr-1"></i>
            {{ formatDateBR(formData.opening_date) }}
          </span>
        </div>
      </div>
    </template>
  </DossierDocumentCard>
</template>

<script>
import DossierDocumentCard from "../dossier/DossierDocumentCard.vue";

// Texto explicativo exibido somente no modo edição.
// Aceita HTML — o link aponta para a consulta pública da Receita Federal.
const EXPLAINER_TEXT = `
  O Comprovante de Inscrição e Situação Cadastral do CNPJ é o documento oficial
  que identifica juridicamente a instituição perante a Receita Federal.<br><br>
  Deve ser anexado o comprovante emitido na consulta pública de CNPJ da Receita Federal,
  contendo os dados cadastrais e a situação cadastral da instituição.<br><br>
  <a href="https://solucoes.receita.fazenda.gov.br/servicos/cnpjreva/cnpjreva_solicitacao.asp"
     target="_blank" rel="noopener">
    Acessar consulta pública de CNPJ – Receita Federal
  </a><br><br>
  O Sistema Clin não valida o conteúdo do documento, apenas registra sua existência
  para fins de organização e apresentação à fiscalização.
`;

export default {
  name: "DossierCnpj",

  components: {
    DossierDocumentCard,
  },

  props: {
    // Modo de exibição herdado da tela pai: 'edit' | 'view'
    mode: {
      type: String,
      default: "edit",
      validator: (v) => ["edit", "view"].includes(v),
    },

    // Dados iniciais vindos da API (tela pai passa após o GET)
    initialData: {
      type: Object,
      default: () => ({}),
    },

    // Inicia colapsado (útil quando vários cards estão na mesma tela)
    startCollapsed: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      explainerText: EXPLAINER_TEXT,

      // Opções de status (seleção única — radio)
      statusOptions: [
        { value: "possui_atualizado", label: "Possui comprovante atualizado" },
        {
          value: "possui_desatualizado",
          label: "Possui comprovante desatualizado",
        },
        { value: "nao_possui", label: "Não possui comprovante (pendente)" },
      ],

      // Estado local do formulário.
      // Inicializado com os dados vindos da prop; o DossierDocumentCard
      // gerencia internamente via v-model a partir daqui.
      formData: {
        cnpj_number: "",
        opening_date: null,
        status: null, // seleção única → string (não array)
        validity_date: null, // CNPJ não tem validade → sempre null
        file_url: null,
        file_name: null,
        observations: "",
        // Mesclamos os dados iniciais recebidos via prop
        ...this.flattenInitialData(),
      },

      rules: {
        required: (v) => !!v || "Campo obrigatório",
        cnpj: (v) => {
          if (!v) return true; // a regra required já captura o vazio
          const digits = v.replace(/\D/g, "");
          return digits.length === 14 || "CNPJ inválido";
        },
      },
    };
  },

  computed: {
    isViewMode() {
      return this.mode === "view";
    },
  },

  watch: {
    // Quando o pai atualiza os dados externos (ex: re-fetch), sincroniza
    initialData: {
      deep: true,
      handler() {
        this.formData = {
          cnpj_number: "",
          opening_date: null,
          status: null,
          validity_date: null,
          file_url: null,
          file_name: null,
          observations: "",
          ...this.flattenInitialData(),
        };
      },
    },
  },

  methods: {
    // ── Normaliza os dados iniciais ────────────────────────────────────────
    // A API pode retornar status como array (padrão do card base) ou string.
    // O CNPJ usa seleção única, então garantimos que seja string ou null.
    flattenInitialData() {
      const d = this.initialData || {};
      return {
        cnpj_number: d.cnpj_number || "",
        opening_date: d.opening_date || null,
        // Normaliza: se vier array (["possui_atualizado"]), pega o primeiro elemento
        status: Array.isArray(d.status)
          ? d.status[0] || null
          : d.status || null,
        validity_date: d.validity_date || null,
        file_url: d.file_url || null,
        file_name: d.file_name || null,
        observations: d.observations || "",
      };
    },

    // ── Callbacks do DossierDocumentCard ──────────────────────────────────
    onSaved(savedData) {
      // Propaga para a tela pai atualizar o índice
      this.$emit("saved", { module: "cnpj", data: savedData });
    },

    onError(errors) {
      this.$emit("error", { module: "cnpj", errors });
    },

    // ── Utilitários ────────────────────────────────────────────────────────
    formatDateBR(dateStr) {
      if (!dateStr) return "—";
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════
   DOSSIER CNPJ
   Estilos específicos para os campos extras deste módulo.
   O visual do card em si (cabeçalho, status, upload, etc.)
   é gerenciado pelo DossierDocumentCard.
   ═══════════════════════════════════════════════════════════════ */

/* ── Grid dos campos extras ──────────────────────────────────── */
.dossier-cnpj__fields {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

/* ── Grupo de campo individual ───────────────────────────────── */
.dossier-cnpj__field-group {
  flex: 1;
  min-width: 200px;
}

/* ── Label ───────────────────────────────────────────────────── */
.dossier-cnpj__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
}

.dossier-cnpj__label--required::after {
  content: " *";
  color: #dc3545;
}

/* ── Inputs ──────────────────────────────────────────────────── */
.dossier-cnpj__input {
  font-size: 13px;
}

.dossier-cnpj__input--date {
  max-width: 220px;
}

/* ── Valor no modo visualização ──────────────────────────────── */
.dossier-cnpj__view-value {
  font-size: 13px;
  color: #333;
}

/* ── Responsivo ──────────────────────────────────────────────── */
@media (max-width: 600px) {
  .dossier-cnpj__fields {
    flex-direction: column;
    gap: 12px;
  }

  .dossier-cnpj__input--date {
    max-width: 100%;
  }
}
</style>